import { useCallback, useRef, useState } from 'react';

import { Button } from '@opendoor/atoms/button';
import { Dialog } from '@opendoor/atoms/dialog';
import { Popover } from '@opendoor/atoms/popover';
import { useAtomsToastManager } from '@opendoor/atoms/toast';
import { IconBookmark } from '@tabler/icons-react';
import { useMediaQuery } from 'components/shared/hooks/useMediaQuery';

import { useObservability } from '../../../helpers/observability';
import type { SavedSearchFieldsFragment } from './__generated__/saved-searches.zeus';
import { ConfirmDeleteDialog } from './ConfirmDeleteDialog';
import { buildRenameInput } from './mapping';
import { SavedSearchesMenu } from './SavedSearchesMenu';
import { useSavedSearches } from './useSavedSearches';

export interface SavedSearchesDropdownProps {
  /**
   * Applies the selected saved search. The host page owns the marketplace
   * search state, so it decides whether to refetch results in place (same
   * market) or hard-navigate for fresh SSR data (different market / zone /
   * polygon).
   */
  onApplySavedSearch: (savedSearch: SavedSearchFieldsFragment) => void;
  /**
   * The saved search currently applied on the page, if any. Its row gets an
   * "Active" marker.
   */
  activeSavedSearchId?: string | null;
  /**
   * Opens the filters panel. Backs the empty state's CTA so a user with no
   * saved searches is pointed at where saving starts.
   */
  onOpenFilters?: () => void;
  /** Size for the trigger button; match the surrounding controls. */
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Saved-searches switcher. Renders for logged-in users — including with zero
 * saved searches, where it teaches the feature instead of hiding (an invisible
 * feature can't be discovered). Desktop gets an anchored popover; small
 * screens get the bottom-sheet dialog.
 */
export const SavedSearchesDropdown = ({
  onApplySavedSearch,
  activeSavedSearchId,
  onOpenFilters,
  size = 'lg',
}: SavedSearchesDropdownProps) => {
  const { trackEvent } = useObservability();
  const toastManager = useAtomsToastManager();
  // `useAtomsToastManager` returns a fresh object each render, so read it
  // through a ref instead of a dependency (matches CopyLinkToast.tsx et al.).
  const toastManagerRef = useRef(toastManager);
  // oxlint-disable-next-line react/refs -- react19-baseline: pre-existing, do not copy. Read refs in handlers or effects, never during render
  toastManagerRef.current = toastManager;
  const { isLoggedIn, savedSearches, update, remove } = useSavedSearches();
  const isDesktop = useMediaQuery('(min-width: 768px)');

  const [isOpen, setIsOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<SavedSearchFieldsFragment | null>(null);

  const handleSelect = useCallback(
    (savedSearch: SavedSearchFieldsFragment) => {
      trackEvent('cta-click', 'saved-search-selected', {
        savedSearchId: savedSearch.id,
        market: savedSearch.location.market,
      });
      onApplySavedSearch(savedSearch);
      setIsOpen(false);
    },
    [onApplySavedSearch, trackEvent],
  );

  const handleRename = useCallback(
    async (savedSearch: SavedSearchFieldsFragment, newName: string) => {
      const result = await update(savedSearch.id, buildRenameInput(savedSearch, newName));
      if (result.success) {
        toastManagerRef.current.add({ title: `Renamed to “${newName}”`, variant: 'success' });
      }
      return result;
    },
    [update],
  );

  const handleConfirmDelete = useCallback(
    async (id: string) => {
      const result = await remove(id);
      if (result.success) {
        toastManagerRef.current.add({ title: 'Saved search deleted', variant: 'success' });
      }
      return result;
    },
    [remove],
  );

  const handleOpenFilters = useCallback(() => {
    setIsOpen(false);
    onOpenFilters?.();
  }, [onOpenFilters]);

  if (!isLoggedIn) {
    return null;
  }

  const count = savedSearches.length;
  const buttonLabel = count > 0 ? `Saved searches (${count})` : 'Saved searches';

  const menuContent = (
    <SavedSearchesMenu
      savedSearches={savedSearches}
      activeSavedSearchId={activeSavedSearchId}
      onSelect={handleSelect}
      onRename={handleRename}
      onDelete={setPendingDelete}
      onOpenFilters={onOpenFilters ? handleOpenFilters : undefined}
    />
  );

  return (
    <>
      {isDesktop ? (
        <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
          <Popover.Trigger
            render={(props) => (
              <Button
                {...props}
                analyticsName="saved-searches-dropdown-trigger"
                aria-label={buttonLabel}
                size={size}
                variant="secondary"
              >
                <IconBookmark size={18} aria-hidden />
                {buttonLabel}
              </Button>
            )}
          />
          <Popover align="end" className="w-100 max-w-[calc(100vw-2rem)]">
            {menuContent}
          </Popover>
        </Popover.Root>
      ) : (
        <>
          <Button
            analyticsName="saved-searches-dropdown-trigger"
            aria-label={buttonLabel}
            onClick={() => setIsOpen(true)}
            size={size}
            variant="secondary"
          >
            <IconBookmark size={18} aria-hidden />
            {buttonLabel}
          </Button>
          <Dialog.Root role="dialog" open={isOpen} onOpenChange={setIsOpen}>
            <Dialog title="Your saved searches">{menuContent}</Dialog>
          </Dialog.Root>
        </>
      )}
      {pendingDelete && (
        <ConfirmDeleteDialog
          savedSearch={pendingDelete}
          isOpen={pendingDelete !== null}
          onClose={() => setPendingDelete(null)}
          onConfirmDelete={handleConfirmDelete}
        />
      )}
    </>
  );
};
